// Quote templates (M3, docs/plans/lens-dealer-portal/quote-templates.html): the
// Templates tab in Quotes, the New quote menu, Phoneware's customer-first New quote
// sheet, the template sheet with rename, delete and the edit conflict, and the
// builder's Save as template sheet and started-from notices. A template keeps
// products, quantities, the term and the PDF note; prices always come from today's
// catalog for its dealer.
import React, { useEffect, useRef, useState } from "react";
import {
  formatCents,
  formatTerm,
  type ContractTerm,
  type CustomerSummary,
  type Dealer,
  type Quote,
  type QuoteLine,
  type QuoteTemplateDetail,
  type UnquotableTemplateLine,
} from "@phoneware/lens-domain";
import { ApiError, type LensApi } from "../api.js";
import { applyMine, conflictRows, editOf, leftOutPrice, nameTaken, productNames, sameEdit, templateMoney, type ConflictRow, type TemplateEdit } from "../api-templates.js";
import { Icon } from "../icons.js";
import { Link, navigate } from "../router.js";
import { useLoad, useSession } from "../session.js";
import { TERMS, errorText, firstName, plural, when, whenIn } from "../format.js";
import { Callout, Chip, QtyBox, Sheet, useToast } from "../ui.js";

type RepName = (email: string) => string;

const byRecent = (a: QuoteTemplateDetail, b: QuoteTemplateDetail): number => b.updatedAt.localeCompare(a.updatedAt) || a.name.localeCompare(b.name);

/** The template the builder just saved, lit up once in the list. */
let freshTemplate: string | null = null;

function duplicateMessage(dealerName: string, name: string): string {
  return `${dealerName} already has a template called \u201c${name}\u201d. Names are unique for each dealer.`;
}

/** "6 lines · $228.00 a month · $2,490.00 once", and how many lines can't be quoted now. */
function TemplateLine({ t }: { t: QuoteTemplateDetail }): React.ReactElement {
  const m = templateMoney(t);
  return (
    <>
      <span>
        {m.out ? `${m.ok} of ${plural(m.lines, "line")}` : plural(m.ok, "line")} · {formatCents(m.monthly)} a month
        {m.oneTime ? ` · ${formatCents(m.oneTime)} once` : ""}
      </span>
      {m.out ? <span className="warn">{plural(m.out, "line")} can&rsquo;t be quoted now</span> : null}
    </>
  );
}

/** Closes a popup on a click outside it or on Escape. */
function useDismiss(open: boolean, box: React.RefObject<HTMLElement | null>, close: () => void): void {
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (box.current && e.target instanceof Node && !box.current.contains(e.target)) close();
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", away);
    window.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("mousedown", away);
      window.removeEventListener("keydown", key);
    };
  }, [open, box, close]);
}

// ------------------------------------------------------------------ New quote

/** A dealer's New quote: a blank quote, one from a quote document (M7), or one of the dealer's templates, which opens the draft. */
export function NewQuoteMenu({ templates, onFromDocument }: { templates: QuoteTemplateDetail[] | null; onFromDocument?: () => void }): React.ReactElement {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useDismiss(open, box, () => setOpen(false));
  const list = [...(templates ?? [])].sort(byRecent);
  return (
    <div className="nq" ref={box}>
      <button className="btn primary" type="button" aria-haspopup="menu" aria-expanded={open} data-testid="quotes-new" onClick={() => setOpen((o) => !o)}>
        <Icon name="plus" size={15} />
        New quote
        <Icon name="chevron-down" size={14} />
      </button>
      {open ? (
        <div className="pop" role="menu" aria-label="New quote" data-testid="new-quote-menu">
          <button className="pi" role="menuitem" type="button" data-testid="new-quote-blank" onClick={() => navigate("/quotes/new")}>
            <Icon name="plus" size={15} />
            <span className="pi-main">
              <b>Blank quote</b>
            </span>
          </button>
          {onFromDocument ? (
            <button
              className="pi"
              role="menuitem"
              type="button"
              data-testid="new-quote-from-document"
              onClick={() => {
                setOpen(false);
                onFromDocument();
              }}
            >
              <Icon name="file-up" size={15} className="doc" />
              <span className="pi-main">
                <b>From a quote document</b>
                <span>A file or pasted text. Omni reads it into a draft you check.</span>
              </span>
            </button>
          ) : null}
          <p className="pop-g">From a template</p>
          {templates === null ? (
            <p className="pop-empty">Loading templates.</p>
          ) : list.length ? (
            list.map((t) => (
              <button
                key={t.id}
                className="pi"
                role="menuitem"
                type="button"
                data-testid={`new-quote-template-${t.id}`}
                onClick={() => navigate(`/quotes/new?from=${encodeURIComponent(t.id)}`)}
              >
                <Icon name="layout-template" size={15} />
                <span className="pi-main">
                  <b>{t.name}</b>
                  <TemplateLine t={t} />
                </span>
              </button>
            ))
          ) : (
            <p className="pop-empty">No templates yet. Save a quote as a template and it shows up here.</p>
          )}
          <hr />
          <button
            className="pi"
            role="menuitem"
            type="button"
            onClick={() => {
              setOpen(false);
              navigate("/quotes/templates");
            }}
          >
            <Icon name="settings" size={15} />
            <span className="pi-main">
              <b>Manage templates</b>
            </span>
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Ops's New quote: the customer first, since a quote belongs to the customer's dealer, then that dealer's
 * templates. Started from a template, it lists only that dealer's customers and the pick opens the draft.
 */
export function OpsNewQuoteSheet({
  from,
  templates,
  dealers,
  onClose,
}: {
  from: QuoteTemplateDetail | null;
  templates: QuoteTemplateDetail[];
  dealers: Dealer[];
  onClose: () => void;
}): React.ReactElement {
  const { api, canWrite, writeScope } = useSession();
  const customers = useLoad<CustomerSummary[]>(() => api.customers(), []);
  const [find, setFind] = useState("");
  const [picked, setPicked] = useState<CustomerSummary | null>(null);
  const dealerName = (id: string) => dealers.find((d) => d.id === id)?.name ?? "";
  // Sales at Phoneware quotes the house dealer's customers only (mock M1); a Super admin quotes any dealer's.
  const houseOnly = writeScope("quotes.write") === "house";
  const quotable = (dealerId: string) => canWrite("quotes.write", dealers.find((d) => d.id === dealerId) ?? null);

  if (picked) {
    const name = dealerName(picked.dealerId);
    const list = templates.filter((t) => t.dealerId === picked.dealerId).sort(byRecent);
    const start = (t: QuoteTemplateDetail | null) =>
      navigate(`/quotes/new?customer=${picked.autotaskCompanyId}${t ? `&from=${encodeURIComponent(t.id)}` : ""}`);
    return (
      <Sheet
        title="New quote"
        onClose={onClose}
        foot={
          <button className="btn" type="button" data-testid="ops-new-back" onClick={() => setPicked(null)}>
            <Icon name="arrow-left" size={14} />
            Pick another customer
          </button>
        }
      >
        <p className="card-note first">
          For <b>{picked.name}</b>, a {name} customer. {name}&rsquo;s templates:
        </p>
        <div className="pop pop-static" data-testid="ops-new-templates">
          <button className="pi" type="button" data-testid="ops-new-blank" onClick={() => start(null)}>
            <Icon name="plus" size={15} />
            <span className="pi-main">
              <b>Blank quote</b>
            </span>
          </button>
          <p className="pop-g">From a template</p>
          {list.length ? (
            list.map((t) => (
              <button key={t.id} className="pi" type="button" data-testid={`ops-new-template-${t.id}`} onClick={() => start(t)}>
                <Icon name="layout-template" size={15} />
                <span className="pi-main">
                  <b>{t.name}</b>
                  <TemplateLine t={t} />
                </span>
              </button>
            ))
          ) : (
            <p className="pop-empty">{name} has no templates yet.</p>
          )}
        </div>
      </Sheet>
    );
  }

  const needle = find.trim().toLowerCase();
  const pool = (customers.data ?? [])
    .filter((c) => quotable(c.dealerId) && (!from || c.dealerId === from.dealerId) && (!needle || `${c.name} ${dealerName(c.dealerId)}`.toLowerCase().includes(needle)))
    .sort((a, b) => a.name.localeCompare(b.name));
  return (
    <Sheet
      title="New quote"
      onClose={onClose}
      foot={
        <button className="btn" type="button" onClick={onClose}>
          Cancel
        </button>
      }
    >
      <p className="card-note first" data-testid={houseOnly ? "ops-new-house-only" : undefined}>
        {houseOnly
          ? "Sales quotes for Phoneware\u2019s own customers. Another dealer\u2019s customer opens view only."
          : from
            ? `Starting from \u201c${from.name}\u201d. Pick one of ${dealerName(from.dealerId)}\u2019s customers.`
            : "Pick the customer first. Their dealer\u2019s templates come next."}
      </p>
      <label className="search ops-find">
        <Icon name="search" size={15} />
        <span className="sr">Find a customer</span>
        <input className="input" type="search" placeholder="Customer or dealer" data-testid="ops-new-search" value={find} onChange={(e) => setFind(e.target.value)} />
      </label>
      {customers.error ? (
        <Callout kind="stop" icon="triangle-alert">
          {customers.error.message}
        </Callout>
      ) : (
        <ul className="rows ops-pool">
          {!customers.data ? (
            <li className="empty">Loading customers.</li>
          ) : pool.length === 0 ? (
            <li className="empty">No customer matches.</li>
          ) : (
            pool.slice(0, 12).map((c) => (
              <li key={c.autotaskCompanyId}>
                <a
                  className="row"
                  href={from ? `/quotes/new?customer=${c.autotaskCompanyId}&from=${encodeURIComponent(from.id)}` : "#"}
                  data-testid={`ops-new-customer-${c.autotaskCompanyId}`}
                  onClick={(e) => {
                    e.preventDefault();
                    if (from) navigate(`/quotes/new?customer=${c.autotaskCompanyId}&from=${encodeURIComponent(from.id)}`);
                    else setPicked(c);
                  }}
                >
                  <span className="row-main">
                    <span className="row-t">{c.name}</span>
                    <span className="row-m">
                      {dealerName(c.dealerId)} · {plural(templates.filter((t) => t.dealerId === c.dealerId).length, "template")}
                    </span>
                  </span>
                  <span className="row-end chev">
                    <Icon name="chevron-right" size={16} />
                  </span>
                </a>
              </li>
            ))
          )}
        </ul>
      )}
    </Sheet>
  );
}

// ------------------------------------------------------------------ the list

type Opened =
  | { kind: "tpl"; base: QuoteTemplateDetail }
  | { kind: "rename"; t: QuoteTemplateDetail; back: boolean }
  | { kind: "delete"; t: QuoteTemplateDetail; back: boolean }
  | { kind: "conflict"; base: QuoteTemplateDetail; theirs: QuoteTemplateDetail; mine: TemplateEdit };

export function TemplatesCard({
  ops,
  templates,
  dealers,
  repName,
  firstDraft,
  onReload,
  onStart,
}: {
  ops: boolean;
  templates: QuoteTemplateDetail[];
  dealers: Dealer[];
  repName: RepName;
  /** A draft quote of the caller's, offered by the empty state. */
  firstDraft: Quote | null;
  onReload: () => void;
  onStart: (t: QuoteTemplateDetail) => void;
}): React.ReactElement {
  const { me, canWrite } = useSession();
  const toast = useToast();
  const [find, setFind] = useState("");
  const [dealerFilter, setDealerFilter] = useState("");
  const [menu, setMenu] = useState<string | null>(null);
  const [opened, setOpened] = useState<Opened | null>(null);
  const [fresh] = useState(() => {
    const id = freshTemplate;
    freshTemplate = null;
    return id;
  });
  const menuBox = useRef<HTMLSpanElement>(null);
  useDismiss(menu !== null, menuBox, () => setMenu(null));

  const dealerOf = (id: string) => (ops ? dealers.find((d) => d.id === id) ?? null : me.dealer);
  const dealerName = (id: string) => dealerOf(id)?.name ?? "";
  // Sales at Phoneware keeps the house dealer's templates; Billing ops and Support read every dealer's.
  const writes = (t: QuoteTemplateDetail) => canWrite("quotes.write", dealerOf(t.dealerId));
  const scoped = templates.filter((t) => !ops || !dealerFilter || t.dealerId === dealerFilter);
  const needle = find.trim().toLowerCase();
  const list = scoped.filter((t) => !needle || t.name.toLowerCase().includes(needle)).sort(byRecent);

  const reopen = (base: QuoteTemplateDetail) => setOpened({ kind: "tpl", base });
  const saved = (next: QuoteTemplateDetail, message: string) => {
    reopen(next);
    onReload();
    toast(message);
  };

  const filters = (
    <div className="filters">
      <label className="search grow">
        <Icon name="search" size={15} />
        <span className="sr">Find a template</span>
        <input className="input" type="search" placeholder="Template name" data-testid="templates-search" value={find} onChange={(e) => setFind(e.target.value)} />
      </label>
      {ops ? (
        <label className="field">
          <span className="sr">Dealer</span>
          <select className="input" aria-label="Dealer" data-testid="templates-dealer" value={dealerFilter} onChange={(e) => setDealerFilter(e.target.value)}>
            <option value="">All dealers</option>
            {[...dealers]
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name} ({templates.filter((t) => t.dealerId === d.id).length})
                </option>
              ))}
          </select>
        </label>
      ) : null}
    </div>
  );

  let body: React.ReactNode;
  if (scoped.length === 0) {
    body = (
      <>
        {ops ? filters : null}
        <EmptyTemplates ops={ops} dealer={ops ? dealers.find((d) => d.id === dealerFilter) ?? null : me.dealer} firstDraft={firstDraft} />
      </>
    );
  } else {
    body = (
      <>
        {filters}
        {list.length === 0 ? (
          <div className="empty">No templates match &ldquo;{find}&rdquo;.</div>
        ) : (
          <div className="tplwrap">
            <table className="t t-tpl" data-testid="templates-list">
              <colgroup>
                <col />
                {ops ? <col className="hide-sm" /> : null}
                <col className="hide-sm" />
                <col />
                <col className="hide-sm" />
                <col className="hide-sm" />
                <col className="c-act" />
              </colgroup>
              <thead>
                <tr>
                  <th>Template</th>
                  {ops ? <th className="hide-sm">Dealer</th> : null}
                  <th className="hide-sm">Term</th>
                  <th className="num">Monthly</th>
                  <th className="num hide-sm">One-time</th>
                  <th className="hide-sm">Last changed</th>
                  <th>
                    <span className="sr">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.map((t) => {
                  const m = templateMoney(t);
                  return (
                    <tr key={t.id} data-template={t.id} className={fresh === t.id ? "fresh" : undefined}>
                      <td className="name">
                        <a
                          className="tn"
                          href="#"
                          data-testid={`template-open-${t.id}`}
                          onClick={(e) => {
                            e.preventDefault();
                            reopen(t);
                          }}
                        >
                          {t.name}
                        </a>
                        <span className="tsub">
                          {plural(t.lines.length, "line")}
                          {ops ? <span className="show-sm"> · {dealerName(t.dealerId)}</span> : null}
                          <span className="show-sm"> · {formatTerm(t.term)}</span>
                          {m.out ? (
                            <span className="chip warn" data-testid={`template-out-${t.id}`}>
                              <Icon name="triangle-alert" size={10} />
                              {plural(m.out, "line")} can&rsquo;t be quoted
                            </span>
                          ) : null}
                        </span>
                      </td>
                      {ops ? <td className="hide-sm">{dealerName(t.dealerId)}</td> : null}
                      <td className="hide-sm">{formatTerm(t.term)}</td>
                      <td className="num mo" data-testid={`template-monthly-${t.id}`}>
                        {formatCents(m.monthly)}
                      </td>
                      <td className="num hide-sm" data-testid={`template-once-${t.id}`}>
                        {formatCents(m.oneTime)}
                      </td>
                      <td className="hide-sm who">
                        {when(t.updatedAt)}
                        <span className="sub">{repName(t.updatedBy)}</span>
                      </td>
                      <td className="act">
                        {writes(t) ? (
                        <>
                        <button className="btn small" type="button" data-testid={`template-start-${t.id}`} onClick={() => onStart(t)}>
                          Start a quote
                        </button>
                        <span className="rowmenu" ref={menu === t.id ? menuBox : undefined}>
                          <button
                            className="iconbtn"
                            type="button"
                            aria-haspopup="menu"
                            aria-expanded={menu === t.id}
                            aria-label={`More for ${t.name}`}
                            data-testid={`template-menu-${t.id}`}
                            onClick={() => setMenu((m2) => (m2 === t.id ? null : t.id))}
                          >
                            <Icon name="ellipsis" size={16} />
                          </button>
                          {menu === t.id ? (
                            <div className="pop small" role="menu">
                              <button
                                className="pi"
                                role="menuitem"
                                type="button"
                                onClick={() => {
                                  setMenu(null);
                                  reopen(t);
                                }}
                              >
                                <Icon name="file-text" size={15} />
                                <span className="pi-main">
                                  <b>Open</b>
                                </span>
                              </button>
                              <button
                                className="pi"
                                role="menuitem"
                                type="button"
                                data-testid={`template-rename-${t.id}`}
                                onClick={() => {
                                  setMenu(null);
                                  setOpened({ kind: "rename", t, back: false });
                                }}
                              >
                                <Icon name="pen-line" size={15} />
                                <span className="pi-main">
                                  <b>Rename</b>
                                </span>
                              </button>
                              <button
                                className="pi danger"
                                role="menuitem"
                                type="button"
                                data-testid={`template-delete-${t.id}`}
                                onClick={() => {
                                  setMenu(null);
                                  setOpened({ kind: "delete", t, back: false });
                                }}
                              >
                                <Icon name="trash-2" size={15} />
                                <span className="pi-main">
                                  <b>Delete</b>
                                </span>
                              </button>
                            </div>
                          ) : null}
                        </span>
                        </>
                        ) : (
                          <Chip kind="quiet" icon="eye" testId={`template-view-only-${t.id}`}>
                            View only
                          </Chip>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="card-note">
          Monthly and one-time are at today&rsquo;s list prices. A template keeps products and quantities; the price comes from the catalog when a quote starts.
        </p>
      </>
    );
  }

  return (
    <>
      <section className="card" data-testid="templates-card">
        {body}
      </section>
      {opened?.kind === "tpl" ? (
        <TemplateSheet
          key={`${opened.base.id}:${opened.base.updatedAt}`}
          base={opened.base}
          dealer={dealerOf(opened.base.dealerId)}
          ops={ops}
          writes={writes(opened.base)}
          repName={repName}
          onClose={() => setOpened(null)}
          onSaved={saved}
          onConflict={(base, theirs, mine) => setOpened({ kind: "conflict", base, theirs, mine })}
          onRename={(t) => setOpened({ kind: "rename", t, back: true })}
          onDelete={(t) => setOpened({ kind: "delete", t, back: true })}
          onStart={(t) => {
            setOpened(null);
            onStart(t);
          }}
        />
      ) : null}
      {opened?.kind === "rename" ? (
        <RenameSheet
          t={opened.t}
          templates={templates}
          dealerName={dealerName(opened.t.dealerId)}
          onCancel={() => (opened.back ? reopen(opened.t) : setOpened(null))}
          onRenamed={(next) => {
            onReload();
            toast(`Renamed to \u201c${next.name}\u201d.`);
            if (opened.back) reopen(next);
            else setOpened(null);
          }}
        />
      ) : null}
      {opened?.kind === "delete" ? (
        <DeleteSheet
          t={opened.t}
          dealerName={dealerName(opened.t.dealerId)}
          repName={repName}
          onCancel={() => (opened.back ? reopen(opened.t) : setOpened(null))}
          onDeleted={() => {
            setOpened(null);
            onReload();
            toast(`Deleted \u201c${opened.t.name}\u201d.`);
          }}
        />
      ) : null}
      {opened?.kind === "conflict" ? (
        <ConflictSheet
          base={opened.base}
          theirs={opened.theirs}
          mine={opened.mine}
          repName={repName}
          onClose={() => setOpened(null)}
          onUseTheirs={() => {
            reopen(opened.theirs);
            onReload();
            toast(`Kept ${repName(opened.theirs.updatedBy)}\u2019s version. Your changes were dropped.`);
          }}
          onSaved={saved}
          onConflict={(theirs) => setOpened({ ...opened, theirs })}
        />
      ) : null}
    </>
  );
}

function EmptyTemplates({ ops, dealer, firstDraft }: { ops: boolean; dealer: Dealer | null; firstDraft: Quote | null }): React.ReactElement {
  const mark = (
    <span className="mark">
      <Icon name="layout-template" size={22} />
    </span>
  );
  if (ops) {
    return (
      <div className="tpl-empty" data-testid="templates-empty">
        {mark}
        {dealer ? (
          <>
            <h2>{dealer.name} has no templates yet</h2>
            <p>{dealer.name}&rsquo;s owner and sales people save them from a quote, and so can Phoneware ops when quoting one of its customers.</p>
          </>
        ) : (
          <>
            <h2>No templates yet</h2>
            <p>Dealers save them from a quote, and so can Phoneware ops when quoting a dealer&rsquo;s customer.</p>
          </>
        )}
      </div>
    );
  }
  return (
    <div className="tpl-empty" data-testid="templates-empty">
      {mark}
      <h2>No templates yet</h2>
      <p>
        Save any quote as a template and it shows up here and under New quote. Its products, quantities, term and PDF note come along. Prices come from the
        catalog each time a quote starts.
      </p>
      <div className="path">
        <span className="step">
          <Icon name="file-text" size={14} />
          Open a quote
        </span>
        <span className="sep">
          <Icon name="chevron-right" size={14} />
        </span>
        <span className="step">
          <Icon name="layout-template" size={14} />
          Save as template
        </span>
        <span className="sep">
          <Icon name="chevron-right" size={14} />
        </span>
        <span className="step">
          <Icon name="plus" size={14} />
          New quote, then the template
        </span>
      </div>
      <div className="btnrow">
        {firstDraft ? (
          <Link className="btn primary" to={`/quotes/${encodeURIComponent(firstDraft.id)}`}>
            Open {firstDraft.number}, {firstDraft.customerName}
          </Link>
        ) : null}
        <button className="btn" type="button" onClick={() => navigate("/quotes/new")}>
          Start a blank quote
        </button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ the template sheet

/** Saves an edit; on 409 reads the template again and, when someone else saved first, hands over the conflict. */
async function saveEdit(
  api: LensApi,
  base: QuoteTemplateDetail,
  edit: TemplateEdit,
  expectedUpdatedAt: string,
): Promise<{ saved: QuoteTemplateDetail } | { theirs: QuoteTemplateDetail }> {
  try {
    return { saved: await api.updateQuoteTemplate(base.id, { term: edit.term, lines: edit.lines, pdfNotes: edit.pdfNotes, expectedUpdatedAt }) };
  } catch (err) {
    if (err instanceof ApiError && err.status === 409) {
      const theirs = await api.quoteTemplate(base.id);
      if (theirs.updatedAt !== expectedUpdatedAt) return { theirs };
    }
    throw err;
  }
}

function TemplateSheet({
  base,
  dealer,
  ops,
  writes,
  repName,
  onClose,
  onSaved,
  onConflict,
  onRename,
  onDelete,
  onStart,
}: {
  base: QuoteTemplateDetail;
  dealer: Dealer | null;
  ops: boolean;
  /** Whether the caller can change this dealer's templates; a Phoneware tier without quotes.write here reads it. */
  writes: boolean;
  repName: RepName;
  onClose: () => void;
  onSaved: (t: QuoteTemplateDetail, message: string) => void;
  onConflict: (base: QuoteTemplateDetail, theirs: QuoteTemplateDetail, mine: TemplateEdit) => void;
  onRename: (t: QuoteTemplateDetail) => void;
  onDelete: (t: QuoteTemplateDetail) => void;
  onStart: (t: QuoteTemplateDetail) => void;
}): React.ReactElement {
  const { api } = useSession();
  const [draft, setDraft] = useState<TemplateEdit>(() => editOf(base));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dirty = !sameEdit(draft, editOf(base));

  const priced = (id: string) => base.pricedLines.find((l) => l.productId === id) ?? null;
  const refused = (id: string) => base.unquotableLines.find((l) => l.productId === id) ?? null;
  const setQty = (productId: string, qty: number) => setDraft((d) => ({ ...d, lines: d.lines.map((l) => (l.productId === productId ? { ...l, qty } : l)) }));
  const remove = (productId: string) => setDraft((d) => ({ ...d, lines: d.lines.filter((l) => l.productId !== productId) }));

  let monthly = 0;
  let oneTime = 0;
  for (const l of draft.lines) {
    const p = priced(l.productId);
    if (p?.billing === "monthly") monthly += p.listPrice * l.qty;
    else if (p) oneTime += p.listPrice * l.qty;
  }
  const outIds = draft.lines.filter((l) => !priced(l.productId)).map((l) => l.productId);

  async function save(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const result = await saveEdit(api, base, draft, base.updatedAt);
      if ("theirs" in result) onConflict(base, result.theirs, draft);
      else onSaved(result.saved, `Saved \u201c${result.saved.name}\u201d.`);
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  const group = (title: string, note: string, ids: string[], out: boolean) =>
    ids.length ? (
      <>
        <div className="group-h">
          <h3>{title}</h3>
          <span>{note}</span>
        </div>
        <div className="tablewrap">
          <table className="t t-tl">
            <colgroup>
              <col />
              <col className="c-qty" />
              <col className="c-each" />
              <col className="c-tot" />
              <col className="c-x" />
            </colgroup>
            <thead>
              <tr>
                <th>Product</th>
                <th>Qty</th>
                <th className="num each">Each</th>
                <th className="num">Total</th>
                <th>
                  <span className="sr">Remove</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {ids.map((id) => {
                const line = draft.lines.find((l) => l.productId === id);
                if (!line) return null;
                const p = priced(id);
                const r = refused(id);
                const name = p?.name ?? r?.productName ?? id;
                return (
                  <tr key={id} className={out ? "out" : undefined} data-testid={`template-line-${id}`}>
                    <td className="l-prod">
                      <b>{name}</b>
                      {p ? <span className="code">{[p.code, p.unit].filter(Boolean).join(" \u00b7 ")}</span> : null}
                      {out ? (
                        <span className="chip warn tl-why">
                          <Icon name="triangle-alert" size={10} />
                          {r?.reason ?? "Product not found in catalog."}
                        </span>
                      ) : null}
                    </td>
                    <td>
                      {writes ? <QtyBox qty={line.qty} onQty={(n) => setQty(id, n)} label={`Quantity of ${name}`} testId={`template-qty-${id}`} /> : line.qty}
                    </td>
                    <td className="num each">
                      {p ? (
                        formatCents(p.listPrice)
                      ) : r && r.listPrice !== null ? (
                        <>
                          <span className={r.period === "yearly" || r.period === "quarterly" || r.period === "semi_annual" ? undefined : "muted"}>{formatCents(r.listPrice)}</span>
                          {r.period === "yearly" ? <span className="sub">a year</span> : r.period === "quarterly" ? <span className="sub">a quarter</span> : r.period === "semi_annual" ? <span className="sub">every six months</span> : null}
                        </>
                      ) : null}
                    </td>
                    <td className="num">{p ? formatCents(p.listPrice * line.qty) : <span className="muted">Left out</span>}</td>
                    <td className="l-x">
                      {writes ? (
                        <button className="iconbtn" type="button" aria-label={`Remove ${name} from the template`} data-testid={`template-remove-${id}`} onClick={() => remove(id)}>
                          <Icon name="x" size={15} />
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </>
    ) : null;

  const ofBilling = (billing: "monthly" | "one_time") => draft.lines.filter((l) => priced(l.productId)?.billing === billing).map((l) => l.productId);

  return (
    <Sheet
      title={base.name}
      wide
      onClose={onClose}
      foot={
        !writes ? (
          <>
            <span className="spacer" />
            <button className="btn" type="button" onClick={onClose}>
              Close
            </button>
          </>
        ) : (
        <>
          <button className="btn danger" type="button" data-testid="template-sheet-delete" onClick={() => onDelete(base)}>
            <Icon name="trash-2" size={15} />
            Delete
          </button>
          <button className="btn" type="button" data-testid="template-sheet-rename" onClick={() => onRename(base)}>
            <Icon name="pen-line" size={15} />
            Rename
          </button>
          <span className="spacer" />
          {dirty ? (
            <>
              <button className="btn" type="button" data-testid="template-discard" onClick={() => setDraft(editOf(base))}>
                Discard changes
              </button>
              <button className="btn primary" type="button" disabled={busy} data-testid="template-save" onClick={() => void save()}>
                <Icon name="check" size={15} />
                {busy ? "Saving" : "Save changes"}
              </button>
            </>
          ) : (
            <button className="btn primary" type="button" data-testid="template-sheet-start" onClick={() => onStart(base)}>
              Start a quote
            </button>
          )}
        </>
        )
      }
    >
      <div className="tl-sheet" data-testid="template-sheet">
        <dl className="tfacts">
          {ops && dealer ? (
            <div>
              <dt>Dealer</dt>
              <dd>
                {dealer.name} <span className="tag">{dealer.model === "agent" ? "Dealer" : "Reseller"}</span>
              </dd>
            </div>
          ) : null}
          <div>
            <dt>Last changed</dt>
            <dd>
              {when(base.updatedAt)} by {repName(base.updatedBy)}
            </dd>
          </div>
          <div>
            <dt>Made</dt>
            <dd>
              {when(base.createdAt)} by {repName(base.createdBy)}
            </dd>
          </div>
        </dl>
        {outIds.length ? (
          <div className="callout warn tl-callout">
            <Icon name="triangle-alert" size={16} />
            <div className="cbody">
              <b>{plural(outIds.length, "line")} can&rsquo;t be quoted now</b>A quote started from this template leaves {outIds.length === 1 ? "it" : "them"} out and names{" "}
              {outIds.length === 1 ? "it" : "them"} with the reason.
            </div>
          </div>
        ) : null}
        <label className="field tl-field">
          <span>Contract term</span>
          <select
            className="input"
            data-testid="template-term"
            disabled={!writes}
            value={String(draft.term)}
            onChange={(e) => setDraft((d) => ({ ...d, term: TERMS.find((t) => String(t) === e.target.value) ?? d.term }))}
          >
            {TERMS.map((t) => (
              <option key={String(t)} value={String(t)}>
                {formatTerm(t)}
              </option>
            ))}
          </select>
        </label>
        {group("Monthly", "Billed every month in OneBill", ofBilling("monthly"), false)}
        {group("One-time", "Billed once, due at signing", ofBilling("one_time"), false)}
        {group("Can\u2019t be quoted now", "Left out when a quote starts", outIds, true)}
        {draft.lines.length ? null : <div className="empty">Every line is removed. Save to keep an empty template, or discard.</div>}
        <div className="tsum" data-testid="template-sum">
          <span>
            At today&rsquo;s prices: <b>{formatCents(monthly)}</b> a month
          </span>
          <span>
            <b>{formatCents(oneTime)}</b> one-time
          </span>
          <span className="muted">Prices are read from the catalog each time a quote starts, so these follow the catalog.</span>
        </div>
        <label className="field tl-field">
          <span>Note for the PDF</span>
          <textarea
            className="input"
            placeholder="What the customer reads above the signature."
            data-testid="template-note"
            readOnly={!writes}
            value={draft.pdfNotes}
            onChange={(e) => setDraft((d) => ({ ...d, pdfNotes: e.target.value }))}
          />
        </label>
        {error ? (
          <Callout kind="stop" icon="triangle-alert">
            {error}
          </Callout>
        ) : null}
      </div>
    </Sheet>
  );
}

function RenameSheet({
  t,
  templates,
  dealerName,
  onCancel,
  onRenamed,
}: {
  t: QuoteTemplateDetail;
  templates: QuoteTemplateDetail[];
  dealerName: string;
  onCancel: () => void;
  onRenamed: (next: QuoteTemplateDetail) => void;
}): React.ReactElement {
  const { api } = useSession();
  const [value, setValue] = useState(t.name);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Moves on when the server says someone else saved it after this sheet opened.
  const [expected, setExpected] = useState(t.updatedAt);

  async function save(): Promise<void> {
    const name = value.trim().replace(/\s+/g, " ");
    const clash = nameTaken(templates, t.dealerId, name, t.id);
    if (clash) {
      setError(duplicateMessage(dealerName, clash.name));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      onRenamed(await api.updateQuoteTemplate(t.id, { name, expectedUpdatedAt: expected }));
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const fresh = await api.quoteTemplate(t.id).catch(() => null);
        if (fresh && fresh.updatedAt !== expected) {
          setExpected(fresh.updatedAt);
          setError(`Someone else saved this template ${whenIn(fresh.updatedAt)}, after you opened it. Its name is now \u201c${fresh.name}\u201d.`);
        } else setError(duplicateMessage(dealerName, name));
      } else setError(errorText(err));
      setBusy(false);
    }
  }

  return (
    <Sheet
      title="Rename template"
      onClose={onCancel}
      foot={
        <>
          <button className="btn" type="button" onClick={onCancel}>
            Cancel
          </button>
          <button className="btn primary" type="button" disabled={!value.trim() || busy} data-testid="rename-save" onClick={() => void save()}>
            Save name
          </button>
        </>
      }
    >
      <label className="field">
        <span>Name</span>
        <input
          className={error ? "input bad" : "input"}
          aria-invalid={error !== null}
          data-testid="rename-name"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setError(null);
          }}
        />
      </label>
      {error ? (
        <p className="err" role="alert" data-testid="rename-error">
          {error}
        </p>
      ) : null}
      <p className="card-note">Everyone at {dealerName} sees the new name in Templates and under New quote.</p>
    </Sheet>
  );
}

function DeleteSheet({
  t,
  dealerName,
  repName,
  onCancel,
  onDeleted,
}: {
  t: QuoteTemplateDetail;
  dealerName: string;
  repName: RepName;
  onCancel: () => void;
  onDeleted: () => void;
}): React.ReactElement {
  const { api } = useSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Moves on when the server says someone else saved it after this sheet opened, so a second Delete is a choice made seeing that.
  const [expected, setExpected] = useState(t.updatedAt);
  async function remove(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.deleteQuoteTemplate(t.id, { expectedUpdatedAt: expected });
      onDeleted();
    } catch (err) {
      const fresh = err instanceof ApiError && err.status === 409 ? await api.quoteTemplate(t.id).catch(() => null) : null;
      if (fresh && fresh.updatedAt !== expected) {
        setExpected(fresh.updatedAt);
        setError(`${repName(fresh.updatedBy)} saved this template ${whenIn(fresh.updatedAt)}, after you opened it. Delete it anyway, or keep it.`);
      } else setError(errorText(err));
      setBusy(false);
    }
  }
  return (
    <Sheet
      title="Delete this template?"
      onClose={onCancel}
      foot={
        <>
          <button className="btn" type="button" onClick={onCancel}>
            Keep it
          </button>
          <button className="btn stopfill" type="button" disabled={busy} data-testid="delete-confirm" onClick={() => void remove()}>
            <Icon name="trash-2" size={15} />
            Delete template
          </button>
        </>
      }
    >
      <div className="tl-sheet" data-testid="delete-sheet">
        <p className="del-p">
          <b>&ldquo;{t.name}&rdquo;</b> comes off {dealerName}&rsquo;s template list and the New quote menu for everyone at {dealerName}.
        </p>
        <p className="card-note">Quotes already started from it keep their lines. A deleted template can&rsquo;t be brought back.</p>
        {error ? (
          <Callout kind="stop" icon="triangle-alert">
            {error}
          </Callout>
        ) : null}
      </div>
    </Sheet>
  );
}

function ConflictSheet({
  base,
  theirs,
  mine,
  repName,
  onClose,
  onUseTheirs,
  onSaved,
  onConflict,
}: {
  base: QuoteTemplateDetail;
  theirs: QuoteTemplateDetail;
  mine: TemplateEdit;
  repName: RepName;
  onClose: () => void;
  onUseTheirs: () => void;
  onSaved: (t: QuoteTemplateDetail, message: string) => void;
  onConflict: (theirs: QuoteTemplateDetail) => void;
}): React.ReactElement {
  const { api } = useSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const who = repName(theirs.updatedBy);
  const first = firstName(who);
  const names = productNames(base, theirs);
  const rows = conflictRows(editOf(base), editOf(theirs), mine);
  const clash = rows.filter((r) => r.clash);
  const label = (r: ConflictRow) => (r.kind === "term" ? "Contract term" : r.kind === "note" ? "Note for the PDF" : names.get(r.productId ?? "") ?? r.productId ?? "");
  const cell = (r: ConflictRow, v: ConflictRow["before"], col: "before" | "theirs" | "mine"): React.ReactNode => {
    if (r.kind === "note") return col === "before" ? "Original" : v !== r.before ? "Changed" : "Same";
    if (v === null) return <span className="muted">Removed</span>;
    return r.kind === "term" ? formatTerm(v as ContractTerm) : String(v);
  };
  const plain = (r: ConflictRow) => (r.kind === "term" ? formatTerm(r.mine as ContractTerm) : r.kind === "note" ? "yours" : r.mine === null ? "removed" : String(r.mine));

  async function save(edit: TemplateEdit, message: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const result = await saveEdit(api, base, edit, theirs.updatedAt);
      if ("theirs" in result) onConflict(result.theirs);
      else onSaved(result.saved, message);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet
      title={`Someone else changed \u201c${base.name}\u201d`}
      wide
      onClose={onClose}
      foot={
        <>
          <button className="btn" type="button" disabled={busy} data-testid="conflict-theirs" onClick={onUseTheirs}>
            Use {first}&rsquo;s version
          </button>
          <button
            className={clash.length ? "btn primary" : "btn"}
            type="button"
            disabled={busy}
            data-testid="conflict-mine"
            onClick={() => void save(mine, `Saved your version over ${who}\u2019s.`)}
          >
            Save mine over {first}&rsquo;s
          </button>
          {clash.length ? null : (
            <button
              className="btn primary"
              type="button"
              disabled={busy}
              data-testid="conflict-both"
              onClick={() => void save(applyMine(editOf(base), editOf(theirs), mine), "Saved. Both changes are in.")}
            >
              <Icon name="check" size={15} />
              Keep both changes
            </button>
          )}
        </>
      }
    >
      <div className="tl-sheet" data-testid="conflict-sheet">
        <div className="callout warn" role="alert">
          <Icon name="triangle-alert" size={16} />
          <div className="cbody">
            <b>
              {who} saved this template {whenIn(theirs.updatedAt)}, after you opened it
            </b>
            Your changes aren&rsquo;t saved yet. Here is what each of you changed.
          </div>
        </div>
        <div className="tablewrap cmp-wrap">
          <table className="t t-cmp">
            <colgroup>
              <col />
              <col className="c-v" />
              <col className="c-v" />
              <col className="c-v" />
            </colgroup>
            <thead>
              <tr>
                <th>What changed</th>
                <th className="num">Before</th>
                <th className="num">{first}</th>
                <th className="num">You</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className={r.clash ? "clash" : undefined} data-testid={`conflict-row-${r.key}`}>
                  <td>
                    {label(r)}
                    {r.clash ? <span className="sub both">You both changed this</span> : null}
                  </td>
                  <td className="num muted">{cell(r, r.before, "before")}</td>
                  <td className={r.theirs !== r.before ? "num theirs" : "num muted"}>{cell(r, r.theirs, "theirs")}</td>
                  <td className={r.mine !== r.before ? "num mine" : "num muted"}>{cell(r, r.mine, "mine")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {clash.length ? (
          <p className="card-note cmp-note">
            Saving yours puts {clash.map((r) => `${label(r)} at ${plain(r)}`).join(", ")}
            {rows.length > clash.length ? ` and undoes the rest of ${first}\u2019s change` : ""}.
          </p>
        ) : (
          <div className="callout ok cmp-note">
            <Icon name="check" size={16} />
            <div className="cbody">You changed different lines, so both changes can be kept.</div>
          </div>
        )}
        {error ? (
          <Callout kind="stop" icon="triangle-alert">
            {error}
          </Callout>
        ) : null}
      </div>
    </Sheet>
  );
}

// ------------------------------------------------------------------ the builder's side

/** A quote started from a template: which one, and the lines it left out because they can't be quoted now. */
export interface StartedFrom {
  template: QuoteTemplateDetail;
  left: UnquotableTemplateLine[];
  removed: boolean;
  busy: boolean;
  problem: string | null;
}

export function StartedFromNotice({ from, onRemove }: { from: StartedFrom; onRemove: () => void }): React.ReactElement {
  const n = from.left.length;
  return (
    <>
      <div className="callout" data-testid="quote-from-template">
        <Icon name="layout-template" size={16} />
        <div className="cbody">
          <b>Started from &ldquo;{from.template.name}&rdquo;</b>
          Products, quantities, term and the PDF note come from the template. Prices come from today&rsquo;s catalog.
        </div>
      </div>
      {n && !from.removed ? (
        <div className="callout warn" role="status" data-testid="quote-left-out">
          <Icon name="triangle-alert" size={16} />
          <div className="cbody">
            <b>
              {plural(n, "line")} from this template can&rsquo;t be quoted now, so {n === 1 ? "it was" : "they were"} left out
            </b>
            <ul className="leftout">
              {from.left.map((l) => (
                <li key={l.productId} data-testid={`quote-left-${l.productId}`}>
                  <span>
                    {l.productName}
                    <small>
                      {l.qty} in the template{leftOutPrice(l) ? ` \u00b7 ${leftOutPrice(l)}` : ""}
                    </small>
                  </span>
                  <span className="chip warn">{l.reason}</span>
                </li>
              ))}
            </ul>
            {from.problem ? <p className="left-problem">{from.problem}</p> : null}
            <div className="btnrow">
              <button className="btn small white" type="button" disabled={from.busy} data-testid="quote-left-remove" onClick={onRemove}>
                <Icon name="trash-2" size={14} />
                Remove {n === 1 ? "it" : "them"} from the template
              </button>
            </div>
          </div>
        </div>
      ) : n ? (
        <div className="callout ok" role="status" data-testid="quote-left-removed">
          <Icon name="check" size={16} />
          <div className="cbody">
            Removed {plural(n, "line")} from &ldquo;{from.template.name}&rdquo;.
          </div>
        </div>
      ) : null}
    </>
  );
}

/** Save as template, from the quote builder: the products, quantities, term and PDF note go; the customer, prices and internal notes stay. */
export function SaveAsTemplateSheet({
  dealer,
  ops,
  lines,
  term,
  pdfNotes,
  customerName,
  onClose,
}: {
  dealer: Dealer | null;
  ops: boolean;
  lines: QuoteLine[];
  term: ContractTerm;
  pdfNotes: string;
  customerName: string | null;
  onClose: () => void;
}): React.ReactElement {
  const { api } = useSession();
  const toast = useToast();
  const existing = useLoad<QuoteTemplateDetail[]>(() => (dealer ? api.quoteTemplates(ops ? dealer.id : undefined) : Promise.resolve([])), [dealer?.id, ops]);
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const dealerName = dealer?.name ?? "";
  const offList = lines.filter((l) => l.unitPrice !== l.listPrice);

  async function save(): Promise<void> {
    if (!dealer) return;
    const name = value.trim().replace(/\s+/g, " ");
    const clash = nameTaken(existing.data ?? [], dealer.id, name, null);
    if (clash) {
      setError(duplicateMessage(dealerName, clash.name));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const saved = await api.createQuoteTemplate({
        ...(ops ? { dealerId: dealer.id } : {}),
        name,
        term,
        lines: lines.map((l) => ({ productId: l.productId, qty: l.qty })),
        pdfNotes,
      });
      freshTemplate = saved.id;
      toast(`Saved \u201c${saved.name}\u201d to ${dealerName}\u2019s templates.`);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError && err.status === 409 ? duplicateMessage(dealerName, name) : errorText(err));
      setBusy(false);
    }
  }

  return (
    <Sheet
      title="Save as template"
      onClose={onClose}
      foot={
        <>
          <button className="btn" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" type="button" disabled={!value.trim() || busy || !dealer} data-testid="saveas-confirm" onClick={() => void save()}>
            <Icon name="layout-template" size={15} />
            Save template
          </button>
        </>
      }
    >
      <div className="tl-sheet" data-testid="saveas-sheet">
        <label className="field">
          <span>Name</span>
          <input
            className={error ? "input bad" : "input"}
            aria-invalid={error !== null}
            data-testid="saveas-name"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setError(null);
            }}
          />
        </label>
        {error ? (
          <p className="err" role="alert" data-testid="saveas-error">
            {error}
          </p>
        ) : null}
        <p className="card-note">
          {dealer
            ? `Saved to ${dealerName}\u2019s templates, where everyone at ${dealerName} who quotes can start from it.`
            : "Pick the customer first."}
        </p>
        <div className="keeps">
          <div>
            <h4>Comes along</h4>
            <ul>
              <li>{plural(lines.length, "product")} and their quantities</li>
              <li>The term, {formatTerm(term).toLowerCase()}</li>
              <li>{pdfNotes ? "The note for the PDF" : "The note for the PDF (empty here)"}</li>
            </ul>
          </div>
          <div>
            <h4>Stays with this quote</h4>
            <ul>
              <li>{customerName ?? "The customer"}</li>
              <li>Unit prices</li>
              <li>Internal notes</li>
            </ul>
          </div>
        </div>
        {offList.length ? (
          <div className="callout keeps-note">
            <Icon name="info" size={16} />
            <div className="cbody">
              {offList.map((l) => `${l.name} is ${formatCents(l.unitPrice)} on this quote.`).join(" ")} A quote from the template starts{" "}
              {offList.length === 1 ? "it" : "them"} at list price, {offList.map((l) => formatCents(l.listPrice)).join(", ")}.
            </div>
          </div>
        ) : null}
        <h3>Products</h3>
        <ul className="saved-list">
          {lines.map((l) => (
            <li key={l.productId}>
              <span>{l.name}</span>
              <span>{l.qty}</span>
            </li>
          ))}
        </ul>
      </div>
    </Sheet>
  );
}
